import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useActivityShopUI } from 'hooks/useActivityShopUI';
import { useLocalization } from 'hooks/useLocalization';
import { Activity } from "interfaces/ActivityInterfaces";
import { Box, Typography } from '@mui/material';

import useCleanText from 'hooks/useCleanText';
import ActivityGridItemQuickInfoBar from 'components/activity-display/layouts/activity-grid/ActivityGridItemQuickInfoBar';
import ActivityGridItemBottomBarContent from 'components/activity-display/layouts/activity-grid/ActivityGridItemBottomBarContent';

//Import ActivityTile.css
import './ActivityTile.css';

interface ActivityTileProps {
	activity: Activity;
}

function ActivityTile({ activity }: ActivityTileProps) {
	const { getStr } = useLocalization();
	const [isHovered, setIsHovered] = useState(false);
	const { setSelectedActivityId } = useActivityShopUI();
	const bottomContentRef = useRef<HTMLDivElement>(null);
	
	const coverUrl = activity.images.main.url ? activity.images.main.url : (
		activity.images.gallery.length > 0 && activity.images.gallery[0].url ? activity.images.gallery[0].url : null
	);

	function handleClick() {
		setSelectedActivityId(activity.id);
	}
	
	return (
		<Link to={`/${getStr('PUBLIC_APP_URL_info')}/${activity.id}/${activity.slug}`} onClick={handleClick} style={{ textDecoration: 'none' }}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
					aspectRatio: '1 / 1',
					backgroundImage: `url(${coverUrl})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					overflow: 'hidden'
				}}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => {setIsHovered(false)}}
				className="tt-activity-tile"
			>
					{!isHovered ? (
						<ActivityTileTopContentTitle activity={activity} />
					) : (
						<ActivityTileTopContentShortDesc activity={activity} bottomContentRef={bottomContentRef} />
					)}
				<ActivityTileBottomContentCartButtons activity={activity} isHovered={isHovered} bottomContentRef={bottomContentRef} />
			</Box>
		</Link>
	);
}

function ActivityTileTopContentTitle({ activity }: { activity: Activity}) {
	return(
		<Box sx={{
			backgroundColor: 'rgba(0,0,0,0.4)'}}
		>
			<ActivityTileContentPadding>
				<div style={{ fontWeight: 'bold', fontSize: '1.2em' }}>
					{activity.title}
				</div>
				<ActivityGridItemQuickInfoBar activity={activity} />
			</ActivityTileContentPadding>
		</Box>
	);
}

function ActivityTileTopContentShortDesc({ activity, bottomContentRef }: { activity: Activity, bottomContentRef: React.RefObject<HTMLDivElement>}) {
	const cleanDescShort = useCleanText(activity.descShort);
	const { getStr } = useLocalization();

	return (
		<Box
			className="activity-tile-top-content"
			sx={{
				backgroundColor: 'rgba(0,0,0,0.6)',
				flexGrow: 1, // This makes the component grow to fill available space
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center', // Center vertically
				alignItems: 'center', // Center horizontally
				overflow: 'hidden',
				textOverflow: 'ellipsis',
			}}
		>
			{cleanDescShort ? (
				<Typography sx={{ textAlign: 'center', padding: '20px' }}>
					{cleanDescShort}
				</Typography>
			) : (
				<Typography
					sx={{
						textAlign: 'center',
						color: '#fff', // Assuming you want the text color to be white
						fontSize: '1.5em',
						textTransform: 'uppercase',
						letterSpacing: '0.1em',
					}}
				>
					{getStr('More_info')}
				</Typography>
			)}
		</Box>
	);
}

function ActivityTileBottomContentCartButtons({ activity, isHovered, bottomContentRef }: { activity: Activity, isHovered: boolean, bottomContentRef: React.RefObject<HTMLDivElement> }) {
	const { getTotalPaxQty, paxArr } = useActivityShopUI();
	const { getActivityInteractionData } = useActivityShopUI();

	const activityInteractionData = getActivityInteractionData(activity.id);

	const totalPaxQty = getTotalPaxQty(paxArr);

	if (!activityInteractionData) return null;
	
	return(
		<Box
			ref={bottomContentRef}
			sx={{
				backgroundColor: (
					isHovered ? (
						'rgba(0,0,0,0.6)'
					) : (
						activityInteractionData.isAdded ? '' : 'rgba(0,0,0,0.4)'
					)
				),
				color: '#fff'
			}}>
			<ActivityTileContentPadding>
				<ActivityGridItemBottomBarContent activity={activity} isHovered={isHovered} totalPaxQty={totalPaxQty} activityInteractionData={activityInteractionData} />
			</ActivityTileContentPadding>
		</Box>
	);
}

function ActivityTileContentPadding({ children }: { children: React.ReactNode }) {
	return (
		<Box sx={{
			padding: '10px 20px'
		}}>
			{children}
		</Box>
	);
}

export default ActivityTile;